/**
 * Patterns matching third-party Obi (Lisa) SDK script URLs that leak into
 * Sentry via instrumented DOM event listeners.
 *
 * MOVEPRO-CRM-M5 / JB / HP: `obi-sdk.standalone.iife.js` attaches a document
 * keydown listener that does `e.key.length` without guarding undefined `key`
 * (IME / dead-key / non-character keydowns). Stack is entirely the CDN bundle
 * — not first-party CRM code. Eager `initObi()` from App.vue loads it on every
 * page today; #1770 (MOVEPRO-CRM-M3) lazy-loads but residual noise remains once
 * Lisa is opened.
 *
 * Keep every pattern Obi-CDN-specific. Do not match generic `node_modules`
 * paths or first-party Vue frames.
 */
export const OBI_SDK_URL_PATTERNS: RegExp[] = [/obi-sdk@/i, /obi-sdk\.standalone/i, /\/obi-sdk\//i];

type SentryFrame = { filename?: string | null; abs_path?: string | null };

type SentryExceptionValue = {
    type?: string | null;
    value?: string | null;
    stacktrace?: { frames?: SentryFrame[] | null } | null;
};

type SentryEventLike = {
    exception?: { values?: SentryExceptionValue[] | null } | null;
};

function frameUrl(frame: SentryFrame): string {
    return frame.filename || frame.abs_path || '';
}

function matchesObiSdkUrl(url: string): boolean {
    return OBI_SDK_URL_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Whether a Sentry event should be dropped because it originated from the
 * third-party Obi SDK, not first-party CRM code.
 */
export function shouldDropObiSdkSentryEvent(
    event: SentryEventLike | undefined,
    hint?: { originalException?: unknown },
): boolean {
    const frames = event?.exception?.values?.flatMap((value) => value.stacktrace?.frames ?? []) ?? [];

    if (frames.some((frame) => matchesObiSdkUrl(frameUrl(frame)))) {
        return true;
    }

    const original = hint?.originalException;
    if (original && typeof original === 'object') {
        const stack = 'stack' in original && typeof original.stack === 'string' ? original.stack : '';
        if (stack && matchesObiSdkUrl(stack)) {
            return true;
        }
    }

    return false;
}
